import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useChat } from '../context/ChatProvider';
import { useForm } from "react-hook-form";
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import LoadingScreen from "./LoadingScreen";
import LineLoginButton from "./LineLoginButton";
import { GoogleLogin } from '@react-oauth/google';
import { BeatLoader } from 'react-spinners';

/* ─── animations ─── */
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
`;

/* ─── layout ─── */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  padding: 3rem 1rem 2rem;
  position: relative;
  overflow: hidden;
`;

const Orb = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 0; overflow: hidden;
  &::before, &::after {
    content: ''; position: absolute; border-radius: 50%; filter: blur(80px);
    animation: ${pulse} 6s ease-in-out infinite;
  }
  &::before { width: 400px; height: 400px; background: rgba(99,102,241,0.2); top: -8%; left: -8%; }
  &::after { width: 350px; height: 350px; background: rgba(236,72,153,0.15); bottom: -5%; right: -6%; animation-delay: 2s; }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  backdrop-filter: blur(16px);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.15);
`;

const LogoText = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #f0abfc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const InviteText = styled.p`
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  line-height: 1.6;

  & strong {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const Separator = styled.div`
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
  width: 100%;
  max-width: 280px;
  text-align: center;
  margin: 0.2rem 0;
`;

const SocialWrapper = styled.div`
  width: 100%;
  max-width: 280px;
  display: flex;
  justify-content: center;
`;

/* ─── form ─── */
const GuestForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 280px;
`;

const Input = styled.input`
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.7rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.2s, background 0.2s;
  min-width: 0;

  &::placeholder { color: rgba(255, 255, 255, 0.3); }
  &:focus { outline: none; border-color: rgba(99,102,241,0.5); background: rgba(255,255,255,0.08); }
`;

const JoinButton = styled.button`
  padding: 0.7rem 1.4rem;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 14px rgba(99,102,241,0.3);
  white-space: nowrap;
  flex-shrink: 0;

  &:hover { transform: translateY(-1px); box-shadow: 0 5px 20px rgba(99,102,241,0.45); }
  &:active { transform: translateY(0); }
`;

const ContinueButton = styled.button`
  width: 100%;
  max-width: 280px;
  padding: 0.7rem 0;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 14px rgba(99,102,241,0.3);

  &:hover { transform: translateY(-1px); box-shadow: 0 5px 20px rgba(99,102,241,0.45); }
  &:active { transform: translateY(0); }
`;

const ErrorText = styled.p`
  font-size: 0.8rem;
  color: #fca5a5;
  margin: 0;
  text-align: center;
  line-height: 1.5;
`;

const SuccessText = styled.p`
  font-size: 0.88rem;
  color: #6ee7b7;
  text-align: center;
  line-height: 1.5;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(30, 27, 75, 0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 10;
  border-radius: 24px;
`;

const LineWrapper = styled.div`
  width: 100%;
  max-width: 280px;
`;

/* ─── helpers ─── */
function useQuery() {
    return new URLSearchParams(useLocation().search);
}

/* ─── component ─── */
const InviteLanding = () => {
    const query = useQuery();
    const initialLoc = query.get('lang') || 'en';
    const initialLangName = query.get('full') || 'English';

    const { i18n } = useTranslation();
    const { socket, user, setUser, setToken, fetchRooms, joinRoom, rooms, setRooms } = useChat();

    useEffect(() => { i18n.changeLanguage(initialLoc); }, [initialLoc, i18n]);

    const { register, handleSubmit, formState: { errors } } = useForm();
    const navigate = useNavigate();
    const [inviteValid, setInviteValid] = useState(false);
    const [roomId, setRoomId] = useState(null);
    const [inviteBy, setInvitedBy] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [showLineJoin, setShowLineJoin] = useState(false);
    const [showAuthContinue, setShowAuthContinue] = useState(false);
    const [showLineJoinSuccess, setShowLineJoinSuccess] = useState(false);

    const { t } = useTranslation();
    const { inviteId } = useParams();

    useEffect(() => {
        setIsLoading(true);
        validateInvite();
        setIsLoading(false);
    }, []);

    const validateInvite = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER}/invite/${inviteId}`, { withCredentials: true });
            const { roomId, invitedBy, authUser, token } = response.data;

            if (roomId) {
                setInviteValid(true);
                setInvitedBy(invitedBy);
                setRoomId(roomId);

                if (authUser && token) {
                    setUser(authUser);
                    setToken(token);
                    setShowAuthContinue(true);
                    if (authUser.lineUserId) setShowLineJoin(true);
                }
                fetchRooms();
            } else {
                setErrorMessage(t('InviteLandingePageInvalid'));
                setIsError(true);
                setInviteValid(false);
            }
        } catch (error) {
            setErrorMessage(t('InviteLandingePageInvalid'));
            setIsError(true);
            setInviteValid(false);
        }
    };

    const handleJoinWithLine = async () => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER}/line/join`, { roomId }, { withCredentials: true });
            if (response.data.success) {
                setShowLineJoinSuccess(true);
                if (socket) socket.emit('line-user-added', { roomId, user });
            }
        } catch (error) {
            setErrorMessage("Problem joining from LINE");
        }
    };

    const handleJoinRoom = async () => {
        joinRoom(roomId);
        setIsLoading(false);
        navigate('/chat');
    };

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER}/guest`, { username: data.username, language: initialLangName, inviteId }, { withCredentials: true });
            const { guest, token, success, error } = response.data;

            if (success) {
                setToken(token);
                setUser(guest);
                await fetchRooms();
                navigate(`/guest-invite?roomId=${roomId}`);
            } else {
                setErrorMessage(error);
                setIsError(true);
                setInviteValid(false);
            }
        } catch (err) {
            setErrorMessage("Sorry, there was a problem processing your invitation.");
            setIsError(true);
            setIsLoading(false);
        }
    };

    const googleSuccess = async (response) => {
        try {
            const backendResponse = await axios.post(`${process.env.REACT_APP_SERVER}/v/gtoken`, { idToken: response.credential }, { withCredentials: true });
            const { token, user } = backendResponse.data;
            if (token && user) {
                setToken(token);
                setUser(user);
                sessionStorage.setItem('user', JSON.stringify(user));
                sessionStorage.setItem('token', JSON.stringify(token));
                sessionStorage.setItem('userLoggedInWithGoogle', "TRUE");
                await handleJoinRoom();
            } else {
                setErrorMessage("Failed to login with Google.");
            }
        } catch (error) {
            setErrorMessage("Failed to login with Google.");
        }
    };

    const googleError = () => { setErrorMessage("Failed to login with Google."); };

    if (isLoading && !inviteValid) return <LoadingScreen />;

    return (
        <PageWrapper>
            <Orb />

            <Card>
                {isLoading && <SpinnerOverlay><BeatLoader color="#a5b4fc" size={12} /></SpinnerOverlay>}

                <LogoRow>
                    <LogoImage src="/babblers/BabbleonLogo.png" alt="BabbleOn" />
                    <LogoText>BabbleOn</LogoText>
                </LogoRow>

                {isError && <ErrorText>{errorMessage}</ErrorText>}

                <InviteText>
                    <strong>{inviteBy}</strong> {t('InviteLandingPageInvitedBy')}
                </InviteText>

                {showAuthContinue ? (
                    <>
                        <ContinueButton onClick={handleJoinRoom}>{t('ContinueButton')}</ContinueButton>
                        {showLineJoin && (
                            <LineWrapper>
                                <LineLoginButton text={`${t('ContinueButton')} LINE`} onInit={handleJoinWithLine} />
                            </LineWrapper>
                        )}
                        {showLineJoinSuccess && <SuccessText>{t('LineJoinSuccess')}</SuccessText>}
                    </>
                ) : (
                    <>
                        <GuestForm onSubmit={handleSubmit(onSubmit)}>
                            <Input
                                {...register("username", { required: true, pattern: /^[a-zA-Z0-9_]+$/ })}
                                placeholder={t('SignupUsernameText')}
                                maxLength={20}
                            />
                            <JoinButton type="submit">{t('JoinChatButton')}</JoinButton>
                        </GuestForm>
                        {errors.username && <ErrorText>{t('SignupUsernameValidate')}</ErrorText>}

                        <Separator>- OR -</Separator>

                        <SocialWrapper>
                            <LineLoginButton text={t('LineSignIn')} extraState={`inviteId=${inviteId}`} />
                        </SocialWrapper>

                        <SocialWrapper>
                            <GoogleLogin
                                clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}
                                buttonText={t('GoogleSignin')}
                                width="280"
                                onSuccess={googleSuccess}
                                onError={googleError}
                                cookiePolicy={'single_host_origin'}
                            />
                        </SocialWrapper>
                    </>
                )}
            </Card>
        </PageWrapper>
    );
};

export default InviteLanding;
